import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { CLASSE_BETON_LABELS, FAMILLE_LABELS, MACHINE_LABELS, type ClasseBeton, type FamilleProduit, type MachineCode, type Produit } from '@/types';
import { useCatalogueGroupes, useCreateProduit, useUpdateProduit } from '@/hooks/useBetonCatalog';
import { useFormulesBeton, useMoules } from '@/hooks/useBetonCommandes';
import { extractError } from '@/api/client';

interface Props {
  open:    boolean;
  onClose: () => void;
  /** Si fourni, mode édition. Sinon création */
  produit?: Produit;
}

interface FormState {
  code:                string;
  famille:             FamilleProduit;
  designation:         string;
  sousFamille:         string;
  groupeId:            string;
  prixVenteFcfa:       string;
  uniteVente:          string;
  longueurM:           string;
  largeurMm:           string;
  hauteurMm:           string;
  epaisseurMm:         string;
  poidsKg:             string;
  volumeBetonM3:       string;
  resistanceFc28:      string;
  norme:               string;
  classeProduit:       string;
  classeBeton:         ClasseBeton | '';
  effortNominalDaN:    string;
  machineFabrication:  string;
  // Préfa Phase 1.2
  formuleBetonId:        string;
  ferraillageKgParPiece: string;
  delaiCureJours:        string;
  mouleIdDefault:        string;
  photoUrl:              string;
  ficheTechniquePdfUrl:  string;
  stockActuel:         string;
  seuilAlerte:         string;
  actif:               boolean;
}

const emptyForm: FormState = {
  code: '', famille: 'POTEAUX_BA', designation: '', sousFamille: '', groupeId: '',
  prixVenteFcfa: '', uniteVente: 'U',
  longueurM: '', largeurMm: '', hauteurMm: '', epaisseurMm: '',
  poidsKg: '', volumeBetonM3: '',
  resistanceFc28: '', norme: '', classeProduit: '', classeBeton: '',
  effortNominalDaN: '', machineFabrication: '',
  formuleBetonId: '', ferraillageKgParPiece: '', delaiCureJours: '',
  mouleIdDefault: '', photoUrl: '', ficheTechniquePdfUrl: '',
  stockActuel: '0', seuilAlerte: '0', actif: true,
};

const toForm = (p: Produit): FormState => ({
  code:               p.code,
  famille:            p.famille,
  designation:        p.designation,
  sousFamille:        p.sousFamille ?? '',
  groupeId:           p.groupeId ?? '',
  prixVenteFcfa:      String(Number(BigInt(p.prixVenteCents)) / 100),
  uniteVente:         p.uniteVente,
  longueurM:          p.longueurM ?? '',
  largeurMm:          p.largeurMm != null ? String(p.largeurMm) : '',
  hauteurMm:          p.hauteurMm != null ? String(p.hauteurMm) : '',
  epaisseurMm:        p.epaisseurMm != null ? String(p.epaisseurMm) : '',
  poidsKg:            p.poidsKg ?? '',
  volumeBetonM3:      p.volumeBetonM3 ?? '',
  resistanceFc28:     p.resistanceFc28 ?? '',
  norme:              p.norme ?? '',
  classeProduit:      p.classeProduit ?? '',
  classeBeton:        p.classeBeton ?? '',
  effortNominalDaN:   p.effortNominalDaN != null ? String(p.effortNominalDaN) : '',
  machineFabrication: p.machineFabrication ?? '',
  formuleBetonId:        p.formuleBetonId ?? '',
  ferraillageKgParPiece: p.ferraillageKgParPiece ?? '',
  delaiCureJours:        p.delaiCureJours != null ? String(p.delaiCureJours) : '',
  mouleIdDefault:        p.mouleIdDefault ?? '',
  photoUrl:              p.photoUrl ?? '',
  ficheTechniquePdfUrl:  p.ficheTechniquePdfUrl ?? '',
  stockActuel:        p.stockActuel,
  seuilAlerte:        p.seuilAlerte,
  actif:              p.actif,
});

export const ProduitFormDialog = ({ open, onClose, produit }: Props) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateProduit();
  const update = useUpdateProduit();
  const groupesQ = useCatalogueGroupes();
  const groupes = groupesQ.data?.groupes ?? [];

  useEffect(() => {
    if (open) {
      const f = produit ? toForm(produit) : emptyForm;
      // Produit sans groupe dont l'ancienne sous-famille porte le nom d'un groupe (« Caniveaux », « Buses »…) : proposé.
      if (produit && !f.groupeId && f.sousFamille) {
        const cle = (x: string) => x.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().replace(/\s+/g, ' ').toLowerCase();
        const g = groupes.find((x) => x.actif && cle(x.libelle) === cle(f.sousFamille));
        if (g) f.groupeId = g.id;
      }
      setForm(f);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, produit, groupesQ.data]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const payload = {
      code:               form.code.trim(),
      famille:            form.famille,
      designation:        form.designation.trim(),
      sousFamille:        form.sousFamille.trim() || undefined,
      // Modifier sans groupe = retirer du groupe (null) ; créer sans groupe = « Non classés ».
      groupeId:           form.groupeId || (produit ? null : undefined),
      prixVenteCents:     Math.round(parseFloat(form.prixVenteFcfa || '0') * 100),
      uniteVente:         form.uniteVente.trim() || 'U',
      longueurM:          form.longueurM ? parseFloat(form.longueurM) : undefined,
      largeurMm:          form.largeurMm ? parseInt(form.largeurMm, 10) : undefined,
      hauteurMm:          form.hauteurMm ? parseInt(form.hauteurMm, 10) : undefined,
      epaisseurMm:        form.epaisseurMm ? parseInt(form.epaisseurMm, 10) : undefined,
      poidsKg:            form.poidsKg ? parseFloat(form.poidsKg) : undefined,
      volumeBetonM3:      form.volumeBetonM3 ? parseFloat(form.volumeBetonM3) : undefined,
      resistanceFc28:     form.resistanceFc28 ? parseFloat(form.resistanceFc28) : undefined,
      norme:              form.norme.trim() || undefined,
      classeProduit:      form.classeProduit.trim() || undefined,
      classeBeton:        (form.classeBeton || undefined) as ClasseBeton | undefined,
      effortNominalDaN:   form.effortNominalDaN ? parseInt(form.effortNominalDaN, 10) : undefined,
      machineFabrication: (form.machineFabrication || undefined) as MachineCode | undefined,
      // Préfa Phase 1.2
      formuleBetonId:        form.formuleBetonId || undefined,
      ferraillageKgParPiece: form.ferraillageKgParPiece ? parseFloat(form.ferraillageKgParPiece) : undefined,
      delaiCureJours:        form.delaiCureJours ? parseInt(form.delaiCureJours, 10) : undefined,
      mouleIdDefault:        form.mouleIdDefault || undefined,
      photoUrl:              form.photoUrl.trim() || undefined,
      ficheTechniquePdfUrl:  form.ficheTechniquePdfUrl.trim() || undefined,
      stockActuel:        parseFloat(form.stockActuel || '0'),
      seuilAlerte:        parseFloat(form.seuilAlerte || '0'),
      actif:              form.actif,
    };

    try {
      if (produit) {
        await update.mutateAsync({ id: produit.id, payload });
      } else {
        await create.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  const submitting = create.isPending || update.isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={produit ? `Modifier — ${produit.code}` : 'Nouveau produit'}
      subtitle={produit ? produit.designation : 'Création d\'une référence catalogue'}
      size="lg"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Code *" required>
            <Input value={form.code} onChange={(e) => set('code', e.target.value)} placeholder="PBA-12-650" required />
          </Field>
          <Field label="Famille *">
            <select
              value={form.famille}
              onChange={(e) => set('famille', e.target.value as FamilleProduit)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-primary)]"
            >
              {(Object.keys(FAMILLE_LABELS) as FamilleProduit[]).map((f) => (
                <option key={f} value={f}>{FAMILLE_LABELS[f]}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Désignation *" required>
          <Input value={form.designation} onChange={(e) => set('designation', e.target.value)} required />
        </Field>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Groupe">
            <select
              value={form.groupeId}
              onChange={(e) => set('groupeId', e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-primary)]"
            >
              <option value="">Non classé</option>
              {groupes.filter((g) => g.actif || g.id === form.groupeId).map((g) => (
                <option key={g.id} value={g.id}>{g.libelle}{g.actif ? '' : ' (désactivé)'}</option>
              ))}
            </select>
          </Field>
          <Field label="Précision (classe, type)">
            <Input value={form.sousFamille} onChange={(e) => set('sousFamille', e.target.value)} placeholder="Classe A, Creux, C25/30…" />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Prix de vente HT (FCFA) *">
            <Input
              type="number" min="0" step="1"
              value={form.prixVenteFcfa}
              onChange={(e) => set('prixVenteFcfa', e.target.value)}
              required
              placeholder="9500"
            />
          </Field>
          <Field label="Unité">
            <select
              value={form.uniteVente}
              onChange={(e) => set('uniteVente', e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
            >
              <option>U</option><option>ML</option><option>M2</option><option>M3</option><option>T</option><option>KG</option>
            </select>
          </Field>
        </div>

        <details className="rounded-md border border-gray-200 p-3 text-sm" open={!!produit}>
          <summary className="cursor-pointer font-medium text-gray-700">Caractéristiques techniques</summary>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Longueur (m)">
              <Input type="number" step="0.01" value={form.longueurM} onChange={(e) => set('longueurM', e.target.value)} />
            </Field>
            <Field label="Largeur (mm)">
              <Input type="number" value={form.largeurMm} onChange={(e) => set('largeurMm', e.target.value)} />
            </Field>
            <Field label="Hauteur (mm)">
              <Input type="number" value={form.hauteurMm} onChange={(e) => set('hauteurMm', e.target.value)} />
            </Field>
            <Field label="Épaisseur (mm)">
              <Input type="number" value={form.epaisseurMm} onChange={(e) => set('epaisseurMm', e.target.value)} />
            </Field>
            <Field label="Poids (kg)">
              <Input type="number" step="0.01" value={form.poidsKg} onChange={(e) => set('poidsKg', e.target.value)} />
            </Field>
            <Field label="Volume béton (m³)">
              <Input type="number" step="0.001" value={form.volumeBetonM3} onChange={(e) => set('volumeBetonM3', e.target.value)} placeholder="0.150" />
            </Field>
            <Field label="fc28 (MPa)">
              <Input type="number" step="0.1" value={form.resistanceFc28} onChange={(e) => set('resistanceFc28', e.target.value)} />
            </Field>
            <Field label="Norme">
              <Input value={form.norme} onChange={(e) => set('norme', e.target.value)} placeholder="NF C 67-200" />
            </Field>
            <Field label="Classe produit">
              <Input value={form.classeProduit} onChange={(e) => set('classeProduit', e.target.value)} placeholder="A" />
            </Field>
            <Field label="Effort nominal (daN)">
              <Input type="number" value={form.effortNominalDaN} onChange={(e) => set('effortNominalDaN', e.target.value)} />
            </Field>
          </div>
        </details>

        <details className="rounded-md border border-emerald-200 bg-emerald-50/30 p-3 text-sm" open={!!produit}>
          <summary className="cursor-pointer font-medium text-emerald-800">⚙️ Préfabriqué — Formulation / Cure / Moule (brief Phase 1)</summary>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Field label="Classe béton (NF EN 206)">
              <select
                value={form.classeBeton}
                onChange={(e) => set('classeBeton', e.target.value as ClasseBeton | '')}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
              >
                <option value="">— Aucune —</option>
                {(Object.keys(CLASSE_BETON_LABELS) as ClasseBeton[]).map((c) => (
                  <option key={c} value={c}>{CLASSE_BETON_LABELS[c]}</option>
                ))}
              </select>
            </Field>
            <Field label="Formule béton liée">
              <FormuleSelect value={form.formuleBetonId} onChange={(v) => set('formuleBetonId', v)} />
            </Field>
            <Field label="Moule par défaut">
              <MouleSelect value={form.mouleIdDefault} onChange={(v) => set('mouleIdDefault', v)} />
            </Field>
            <Field label="Ferraillage (kg / pièce)">
              <Input type="number" step="0.01" value={form.ferraillageKgParPiece} onChange={(e) => set('ferraillageKgParPiece', e.target.value)} placeholder="8.5" />
            </Field>
            <Field label="Délai cure (jours)">
              <Input type="number" value={form.delaiCureJours} onChange={(e) => set('delaiCureJours', e.target.value)} placeholder="21" />
            </Field>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="URL photo produit">
              <Input value={form.photoUrl} onChange={(e) => set('photoUrl', e.target.value)} placeholder="https://..." />
            </Field>
            <Field label="URL fiche technique PDF">
              <Input value={form.ficheTechniquePdfUrl} onChange={(e) => set('ficheTechniquePdfUrl', e.target.value)} placeholder="https://..." />
            </Field>
          </div>
        </details>

        <details className="rounded-md border border-gray-200 p-3 text-sm">
          <summary className="cursor-pointer font-medium text-gray-700">Production & Stock</summary>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Machine fabrication">
              <select
                value={form.machineFabrication}
                onChange={(e) => set('machineFabrication', e.target.value)}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
              >
                <option value="">— Aucune —</option>
                {(Object.keys(MACHINE_LABELS) as MachineCode[]).map((m) => (
                  <option key={m} value={m}>{MACHINE_LABELS[m]}</option>
                ))}
              </select>
            </Field>
            {/* Ce champ n'est PAS la disponibilité du catalogue. Celle-ci se
                déduit des lots au parc et ne se saisit pas. Laisser « Stock
                actuel » ici faisait croire qu'on pouvait corriger un stock à la
                main, alors que le chiffre saisi n'apparaît nulle part comme
                disponibilité. */}
            <Field label="Cumul de production">
              <Input type="number" step="0.01" value={form.stockActuel} onChange={(e) => set('stockActuel', e.target.value)} />
              <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--tx-3, #6B7280)' }}>
                Compteur historique, sans les sorties. La disponibilité se lit au parc.
              </p>
            </Field>
            <Field label="Seuil alerte">
              <Input type="number" step="0.01" value={form.seuilAlerte} onChange={(e) => set('seuilAlerte', e.target.value)} />
            </Field>
          </div>
        </details>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.actif} onChange={(e) => set('actif', e.target.checked)} />
          Produit actif
        </label>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>Annuler</Button>
          <Button type="submit" loading={submitting}>{produit ? 'Enregistrer' : 'Créer'}</Button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}{required && ' *'}</label>
    {children}
  </div>
);

const FormuleSelect = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const { data: formules = [] } = useFormulesBeton();
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
    >
      <option value="">— Aucune —</option>
      {formules.filter((f) => f.actif).map((f) => (
        <option key={f.id} value={f.id}>{f.code} · {CLASSE_BETON_LABELS[f.classeBeton]}</option>
      ))}
    </select>
  );
};

const MouleSelect = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const { data: moulesData } = useMoules({ limit: 200 });
  const moules = moulesData?.data ?? [];
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
    >
      <option value="">— Aucun —</option>
      {moules.filter((m) => m.statut !== 'hors_service').map((m) => (
        <option key={m.id} value={m.id}>{m.code} · {m.libelle.slice(0, 30)}</option>
      ))}
    </select>
  );
};
